// src/utils/socketClient.js
//
// Centralized Socket.IO client (browser)
// ======================================
//
// PURPOSE
// -------
// This module provides exactly ONE Socket.IO client instance per browser tab.
//
// It is intentionally a thin realtime transport layer.
// It does NOT own:
// - login
// - logout
// - token refresh
// - auth redirects
// - authoritative data loading
//
// Those responsibilities already belong to:
// - AuthContext
// - apiClient
// - backend auth/session middleware
//
// CURRENT APP ARCHITECTURE
// ------------------------
// Your app follows this model:
//
// 1) HTTP is authoritative
//    - dashboard data comes from GET /my-page
//    - work item details come from HTTP
//    - chat messages are created via HTTP
//
// 2) Socket.IO is invalidation / notification only
//    - realtime events hint that something changed
//    - frontend reacts by updating local UI or refetching HTTP data
//
// 3) Auth/session model
//    - cookies are shared across tabs
//    - X-Session-Id is TAB-LOCAL
//    - different users may coexist in different tabs on the same device/browser
//
// IMPORTANT SECURITY DECISION
// ---------------------------
// This file is aligned with the STRICT TAB-SELECTED AUTH policy.
//
// That means:
// - no registry rehydrate
// - no duplicate-tab session reuse logic here
// - no lineage/source recovery
// - no session discovery
// - no attempt to guess or restore another session for this tab
//
// If THIS TAB does not currently have a valid local session selector,
// socket connection must fail closed:
//
//   -> no socket connection
//
// This is intentional and safer for:
// - shared computers
// - multiple users on one device
// - hard refresh after partial runtime loss
// - broken frontend deployments
//
// DESIGN GOALS
// ------------
// ✅ Exactly one socket instance per browser tab
// ✅ Connect only when this tab can safely identify its own session
// ✅ Never invent auth or perform logout decisions
// ✅ Preserve desired work-item room subscriptions across reconnects
// ✅ Keep BIGINT-safe work item keys as strings
// ✅ Align reconnect timing with AuthContext bootstrap and refresh model
//
// IMPORTANT DESIGN DECISION
// -------------------------
// This module fully trusts the higher-level auth flow:
//
// - AuthContext is responsible for deciding when auth is valid
// - AuthContext is responsible for calling refreshAuthAndReconnect()
// - apiClient is responsible for token refresh
//
// Therefore socketClient remains conservative:
// - use current tab-local selector only
// - never recover/restore anything on its own
// - never force auth semantics on its own
//
// WHY THIS MATTERS
// ----------------
// Realtime instability often comes from multiple layers trying to be smart:
//
// - AuthContext restores/discovers session
// - apiClient recovers session on 401
// - socketClient also tries to restore something
//
// That creates races and unsafe edge cases.
//
// In this version, socketClient is intentionally simple:
// it follows the session state already established elsewhere.

import io from "socket.io-client";
import {
  getSessionId,
  getCurrentSessionSnapshot,
} from "./sessionIdentity";
import { getDeviceId, getBrowserId } from "./clientIdentity";
import { SOCKET_EVENTS } from "./socketEvents";

const DEV = process.env.NODE_ENV !== "production";

/* -------------------------------------------------------------------------- */
/* Server URL                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Socket.IO backend origin.
 *
 * This should point to the same backend that serves:
 * - REST API
 * - Socket.IO namespace
 */
const API_URL =
  process.env.REACT_APP_API_URL || (DEV ? "http://localhost:3001" : "");

/* -------------------------------------------------------------------------- */
/* Module-private singleton state                                             */
/* -------------------------------------------------------------------------- */

/**
 * The singleton socket instance for THIS browser tab.
 *
 * Important:
 * - one JS runtime tab => one module instance => one socket singleton
 */
let socket = null;

/**
 * Whether the application currently WANTS realtime connection.
 *
 * Why we need this:
 * - some pages need realtime (dashboard/chat/work items)
 * - auth cleanup may temporarily force socket offline
 * - we must distinguish:
 *     "socket currently offline but desired"
 *     vs
 *     "socket should stay offline"
 */
let desiredConnected = false;

/**
 * Desired protected work-item room subscriptions.
 *
 * This is an INTENT set, not a confirmed backend state set.
 * On reconnect, we re-emit join requests for every desired key.
 */
const desiredWorkItemKeys = new Set();

/**
 * Tiny monotonic reconnect counter.
 *
 * Purely diagnostic.
 */
let reconnectGeneration = 0;

/**
 * Last backend process runtime id handled by the socket client.
 *
 * This prevents duplicate reconnects when several normal REST responses carry
 * the same new runtime fingerprint after a backend restart.
 */
let lastHandledBackendRuntimeId = "";

/**
 * Small throttle for runtime-change reconnects.
 *
 * The runtime fingerprint event can be observed by several concurrent REST
 * responses. We want one clean reconnect, not a burst of disconnect/connect
 * cycles.
 */
let lastBackendRuntimeReconnectAt = 0;
const BACKEND_RUNTIME_RECONNECT_THROTTLE_MS = 5000;

/**
 * Delay used after the backend sends the graceful-shutdown hint.
 *
 * The backend emits `serverRestarting` shortly before closing Socket.IO.  We do
 * not reconnect immediately because the old process is still shutting down and
 * the new process may not be listening yet.  A short delay lets the transport
 * close first while still making recovery faster than waiting for a long
 * heartbeat timeout.
 */
const SERVER_RESTART_RECONNECT_DELAY_MS = 1200;

let serverRestartReconnectTimer = null;

/* -------------------------------------------------------------------------- */
/* Dev logging helpers                                                        */
/* -------------------------------------------------------------------------- */

function dlog(...args) {
  if (!DEV) return;
  // eslint-disable-next-line no-console
  console.log(...args);
}

function dwarn(...args) {
  if (!DEV) return;
  // eslint-disable-next-line no-console
  console.warn(...args);
}

function clearServerRestartReconnectTimer() {
  if (serverRestartReconnectTimer) {
    clearTimeout(serverRestartReconnectTimer);
    serverRestartReconnectTimer = null;
  }
}

/* -------------------------------------------------------------------------- */
/* Session / auth helpers                                                     */
/* -------------------------------------------------------------------------- */

/**
 * Return current tab-local session id only.
 *
 * IMPORTANT:
 * - no rehydrate
 * - no fallback
 * - no recovery
 *
 * This is the exact behavior required by the strict security model.
 */
function peekTabSessionId() {
  return getCurrentSessionSnapshot().sessionId || "";
}

/**
 * Resolve session id for socket handshake.
 *
 * STRICT TAB-SELECTED POLICY:
 * - use current tab-local selector only
 * - if missing, return empty string
 *
 * We intentionally do NOT:
 * - restore from registry
 * - discover from other tabs
 * - derive from lineage/source tab
 */
function resolveSocketSessionId() {
  return getSessionId({ rehydrate: false }) || "";
}

/**
 * Build the latest handshake auth payload.
 *
 * We compute it fresh right before connect/reconnect because session context may
 * have changed after:
 * - login
 * - refresh
 * - logout
 * - local auth cleanup
 * - prepare-for-login-in-this-tab flow
 * - bootstrap auth check
 */
function buildAuthPayload() {
  return {
    sessionId: resolveSocketSessionId() || null,
    deviceId: getDeviceId(),
    browserId: getBrowserId(),
  };
}

/**
 * Return true if handshake auth is sufficient for a meaningful connect attempt.
 *
 * Backend expects:
 * - sessionId
 * - deviceId
 * - browserId
 */
function canConnectWithAuth(auth) {
  return Boolean(auth?.sessionId && auth?.deviceId && auth?.browserId);
}

/**
 * Return true if the socket's current auth payload still matches the latest
 * tab-known auth payload.
 *
 * Why this matters:
 * - after login / refresh / bootstrap sync, socket.auth may already be stale
 * - reconnect should happen only when meaningful
 */
function doesSocketAuthMatchLatest(socketInstance) {
  if (!socketInstance) return false;

  const current = socketInstance.auth || {};
  const latest = buildAuthPayload();

  return (
    String(current.sessionId || "") === String(latest.sessionId || "") &&
    String(current.deviceId || "") === String(latest.deviceId || "") &&
    String(current.browserId || "") === String(latest.browserId || "")
  );
}

/* -------------------------------------------------------------------------- */
/* BIGINT-safe work item key normalization                                    */
/* -------------------------------------------------------------------------- */

/**
 * Normalize a work item key to canonical frontend form:
 *
 *   "<typeLower>:<idDigits>"
 *
 * Rules:
 * - exactly one colon
 * - type: letters / digits / underscore / dash
 * - id: 1..20 digits
 *
 * Examples:
 * - "todo:123"               -> "todo:123"
 * - "Leave:9223372036854"    -> "leave:9223372036854"
 *
 * Returns:
 * - normalized key string
 * - "" when invalid
 */
function normalizeWorkItemKey(raw) {
  const s = String(raw || "").trim();
  if (!s || s.length > 128) return "";

  const parts = s.split(":");
  if (parts.length !== 2) return "";

  const typeRaw = String(parts[0] || "").trim();
  const idStr = String(parts[1] || "").trim();

  if (!typeRaw || !/^[a-zA-Z0-9_-]{1,40}$/.test(typeRaw)) return "";
  if (!idStr || !/^[0-9]{1,20}$/.test(idStr)) return "";

  return `${typeRaw.toLowerCase()}:${idStr}`;
}


/* -------------------------------------------------------------------------- */
/* Graceful backend restart handling                                          */
/* -------------------------------------------------------------------------- */

/**
 * Schedule one clean reconnect after the backend announces a controlled
 * shutdown/restart.
 *
 * This is intentionally NOT a health-check loop and NOT a /my-page refresh.
 * It only prepares the Socket.IO transport to reconnect with the current
 * tab-local session identity after the old backend process closes its sockets.
 *
 * Dashboard remains responsible for the authoritative catch-up through its
 * existing "socket connected/reconnected -> GET /my-page" behavior.
 */
function scheduleReconnectAfterServerRestart(payload = {}) {
  if (!desiredConnected) {
    return { ok: false, reason: "connection-not-desired" };
  }

  const auth = buildAuthPayload();

  if (!canConnectWithAuth(auth)) {
    if (DEV) {
      dwarn("[socketClient] serverRestarting ignored: valid local session selector is unavailable", {
        reason: payload?.reason || null,
      });
    }
    return { ok: false, reason: "missing-auth" };
  }

  clearServerRestartReconnectTimer();

  if (DEV) {
    dlog("[socketClient] serverRestarting received -> scheduling clean reconnect", {
      reason: payload?.reason || null,
      runtimeId: payload?.runtimeId || null,
      processId: payload?.processId || null,
      delayMs: SERVER_RESTART_RECONNECT_DELAY_MS,
    });
  }

  serverRestartReconnectTimer = setTimeout(() => {
    serverRestartReconnectTimer = null;

    if (!desiredConnected) {
      return;
    }

    const latestAuth = buildAuthPayload();

    if (!canConnectWithAuth(latestAuth)) {
      if (DEV) {
        dwarn("[socketClient] delayed server-restart reconnect skipped: valid local session selector is unavailable");
      }
      return;
    }

    const s = ensureSocket();
    s.auth = latestAuth;
    reconnectGeneration += 1;

    if (DEV) {
      dlog("[socketClient] executing clean reconnect after backend shutdown hint", {
        socketId: s.id || null,
        wasConnected: Boolean(s.connected),
        generation: reconnectGeneration,
      });
    }

    /**
     * If the server closed the namespace with an "io server disconnect" reason,
     * Socket.IO may not automatically reconnect.  Calling connect() explicitly
     * is therefore the safest low-impact action after a controlled shutdown.
     */
    try {
      if (s.connected) {
        s.disconnect();
      }
    } catch {
      // ignore transient disconnect errors
    }

    try {
      s.auth = buildAuthPayload();
    } catch {
      // keep latestAuth prepared above
    }

    s.connect();
  }, SERVER_RESTART_RECONNECT_DELAY_MS);

  return { ok: true, reason: "reconnect-scheduled" };
}

/* -------------------------------------------------------------------------- */
/* Socket creation / lifecycle                                                */
/* -------------------------------------------------------------------------- */

/**
 * Create the singleton socket instance if missing.
 *
 * Important:
 * - autoConnect:false is intentional
 * - we only connect when:
 *     1) app wants connection
 *     2) current tab has valid local auth/session handshake data
 */
function ensureSocket() {
  if (socket) return socket;

  if (!API_URL && DEV) {
    dwarn("[socketClient] REACT_APP_API_URL is empty. Socket may connect to current origin.");
  }

  socket = io(API_URL, {
    withCredentials: true,
    transports: ["websocket", "polling"],
    autoConnect: false,

    /**
     * Auth payload is refreshed again before each explicit connect/reconnect,
     * but we initialize it here so the instance always starts consistent.
     */
    auth: buildAuthPayload(),

    reconnection: true,
    /**
     * Backend restarts must not permanently kill realtime for an already-open
     * authenticated tab. Keep Socket.IO retrying while the app still wants a
     * realtime connection. Explicit logout/public-shell cleanup still calls
     * socketClient.disconnect().
     */
    reconnectionAttempts: Infinity,
    reconnectionDelay: 600,
    reconnectionDelayMax: 4000,
  });

  /* ----------------------- manager reconnect lifecycle ----------------------- */

  /**
   * Keep the Socket.IO handshake payload current before the built-in reconnect
   * attempt. This is intentionally small: it does not poll, does not refresh
   * /my-page and does not make auth decisions. It only ensures that, after a
   * backend restart, the reconnect handshake carries the current tab-local
   * session/device/browser identifiers.
   */
  if (socket.io) {
    socket.io.on("reconnect_attempt", () => {
      try {
        socket.auth = buildAuthPayload();
      } catch {
        // Keep Socket.IO's reconnect attempt unchanged if the auth snapshot
        // cannot be read for any transient reason.
      }
    });
  }

  /* ------------------------------ connect ------------------------------ */

  socket.on(SOCKET_EVENTS.CONNECT, () => {
    clearServerRestartReconnectTimer();

    dlog("✅ [socketClient] connected", {
      socketId: socket?.id,
      sessionId: socket?.auth?.sessionId || null,
      generation: reconnectGeneration,
    });

    /**
     * Refresh local auth payload after successful connect too.
     * This keeps diagnostics aligned with current frontend session state.
     */
    try {
      socket.auth = buildAuthPayload();
    } catch {
      // ignore
    }

    /**
     * Rejoin desired protected work-item rooms best-effort.
     *
     * This is intentionally idempotent:
     * reconnects are normal,
     * and backend remains authoritative for room membership validation.
     */
    for (const key of desiredWorkItemKeys) {
      try {
        socket.emit(SOCKET_EVENTS.JOIN_WORK_ITEM, { workItemKey: key }, (ack) => {
          if (ack && ack.ok === false) {
            /**
             * If backend explicitly denies rejoin, remove room from desired intents.
             *
             * This avoids retrying forever on future reconnects.
             */
            desiredWorkItemKeys.delete(key);

            if (DEV) {
              dwarn("[socketClient] auto-rejoin denied", { key, ack });
            }
          }
        });
      } catch {
        // ignore single-room join failure
      }
    }
  });

  /* ----------------------------- disconnect ---------------------------- */

  socket.on(SOCKET_EVENTS.DISCONNECT, (reason) => {
    dlog("❌ [socketClient] disconnected", {
      reason,
      generation: reconnectGeneration,
    });

    /**
     * Important:
     * We do NOT clear desiredWorkItemKeys here.
     *
     * Why?
     * - transport disconnects are normal
     * - reconnect should preserve room intent
     *
     * desiredWorkItemKeys are only cleared by explicit public disconnect(),
     * which represents deliberate local auth/session teardown.
     */

    /**
     * If the server intentionally closed the namespace during graceful shutdown,
     * some Socket.IO client versions treat this as an explicit server-side
     * disconnect and do not automatically reconnect.  Since this module knows
     * whether the app still wants realtime, it can safely schedule one reconnect
     * attempt with the current tab-local session identity.
     */
    if (reason === "io server disconnect" && desiredConnected) {
      scheduleReconnectAfterServerRestart({ reason: "io-server-disconnect" });
    }
  });

  /* ---------------------------- connect error -------------------------- */

  socket.on(SOCKET_EVENTS.CONNECT_ERROR, (err) => {
    /**
     * connect_error is intentionally NOT treated as a logout signal.
     *
     * Reasons can include:
     * - backend unavailable
     * - proxy/CORS issues
     * - temporary auth mismatch
     * - reconnect during backend restart
     *
     * AuthContext/apiClient remain the owners of real auth decisions.
     */
    dwarn("❌ [socketClient] connect_error", err?.message || err);
  });

  /* ------------------------ controlled server restart --------------------- */

  socket.on(SOCKET_EVENTS.SERVER_RESTARTING, (payload) => {
    /**
     * The backend emits this event only during graceful process shutdown.  It is
     * a transport lifecycle hint, not an auth event and not a business event.
     *
     * We schedule one clean reconnect.  Dashboard will perform its existing
     * one-shot /my-page catch-up after the reconnect emits SOCKET_EVENTS.CONNECT.
     */
    scheduleReconnectAfterServerRestart(payload);
  });

  /* ---------------------------- join denied ---------------------------- */

  socket.on(SOCKET_EVENTS.JOIN_DENIED, (payload) => {
    /**
     * Backend refused a protected room join.
     *
     * This is useful for:
     * - diagnostics
     * - future UX improvements
     *
     * But it must NOT trigger hard auth actions here.
     */
    dwarn("[socketClient] joinDenied", payload);
  });

  return socket;
}

/* -------------------------------------------------------------------------- */
/* Internal helper: attempt connect if desired                                */
/* -------------------------------------------------------------------------- */

/**
 * Connect only if:
 * - app wants realtime
 * - handshake auth is sufficient
 * - socket is not already correctly connected
 *
 * IMPORTANT:
 * No session recovery happens here.
 * Missing sessionId simply means: do not connect.
 */
function connectIfDesired() {
  const s = ensureSocket();

  if (!desiredConnected) {
    return;
  }

  const auth = buildAuthPayload();

  if (!canConnectWithAuth(auth)) {
    if (DEV) {
      dwarn(
        "[socketClient] Not connecting: this tab does not currently have a valid local session selector."
      );
    }
    return;
  }

  /**
   * Already connected with correct auth payload -> nothing to do.
   */
  if (s.connected && doesSocketAuthMatchLatest(s)) {
    return;
  }

  /**
   * Connected with stale auth payload -> reconnect so backend gets fresh handshake.
   */
  if (s.connected && !doesSocketAuthMatchLatest(s)) {
    s.auth = auth;
    reconnectGeneration += 1;

    try {
      s.disconnect();
    } catch {
      // ignore
    }

    s.connect();
    return;
  }

  /**
   * Normal connect path.
   */
  s.auth = auth;
  s.connect();
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Request realtime connection for this tab.
 *
 * Idempotent:
 * - multiple connect() calls are safe
 * - if auth/session is not yet available, socket remains offline
 *
 * Typical consumers:
 * - dashboard
 * - work item details
 * - work item chat
 */
function connect() {
  desiredConnected = true;
  connectIfDesired();
}

/**
 * Fully disconnect realtime for THIS TAB and forget room intents.
 *
 * Use this for:
 * - explicit logout
 * - local auth invalidation
 * - "prepare this tab for different login"
 * - full teardown when realtime must stop
 */
function disconnect() {
  desiredConnected = false;
  clearServerRestartReconnectTimer();

  if (socket) {
    try {
      socket.disconnect();
    } catch {
      // ignore
    }
  }

  /**
   * Drop instance entirely.
   *
   * Why:
   * - next connect() should start from a clean handshake
   * - avoids stale auth payload or stale connection state
   */
  socket = null;

  /**
   * When auth/session is torn down locally, desired work-item rooms are no longer valid.
   */
  desiredWorkItemKeys.clear();
}

/**
 * Refresh handshake auth and reconnect if the app still wants connection.
 *
 * Call this after:
 * - login success
 * - successful auth bootstrap
 * - refresh success that confirmed/rotated session identity
 *
 * IMPORTANT:
 * This is the main alignment point with AuthContext.
 * AuthContext decides when session identity is authoritative enough.
 * socketClient only follows that decision.
 */
function refreshAuthAndReconnect() {
  const s = ensureSocket();
  const nextAuth = buildAuthPayload();

  s.auth = nextAuth;

  if (!desiredConnected) {
    return;
  }

  if (!canConnectWithAuth(nextAuth)) {
    if (DEV) {
      dwarn("[socketClient] Not reconnecting: valid local session selector is unavailable.");
    }
    return;
  }

  /**
   * Already connected with matching auth payload -> do nothing.
   */
  if (s.connected && doesSocketAuthMatchLatest(s)) {
    return;
  }

  reconnectGeneration += 1;

  /**
   * Force reconnect so backend sees newest handshake payload.
   */
  if (s.connected) {
    try {
      s.disconnect();
    } catch {
      // ignore
    }
  }

  s.connect();
}

/**
 * Subscribe to a socket event.
 *
 * Returns:
 *   unsubscribe() function
 *
 * Safe for React effects.
 */
function on(eventName, handler) {
  const s = ensureSocket();

  if (typeof handler !== "function") {
    if (DEV) {
      dwarn("[socketClient] on() called with non-function handler", { eventName });
    }
    return () => {};
  }

  s.on(eventName, handler);

  return () => {
    try {
      s.off(eventName, handler);
    } catch {
      // ignore
    }
  };
}

/* -------------------------------------------------------------------------- */
/* Work item room management                                                  */
/* -------------------------------------------------------------------------- */

/**
 * Join one protected work-item room.
 *
 * Behavior:
 * - validate + normalize key
 * - store desired intent
 * - if socket offline, queue only
 * - if socket online, emit immediately
 */
function joinWorkItem(workItemKey) {
  const key = normalizeWorkItemKey(workItemKey);
  if (!key) {
    return Promise.resolve({ ok: false, error: "INVALID_KEY" });
  }

  desiredWorkItemKeys.add(key);

  const s = ensureSocket();

  if (!s.connected) {
    return Promise.resolve({
      ok: true,
      queued: true,
      workItemKey: key,
    });
  }

  return new Promise((resolve) => {
    try {
      s.emit(SOCKET_EVENTS.JOIN_WORK_ITEM, { workItemKey: key }, (ack) => {
        if (ack && ack.ok === false) {
          desiredWorkItemKeys.delete(key);
        }
        resolve(ack || { ok: true });
      });
    } catch {
      desiredWorkItemKeys.delete(key);
      resolve({ ok: false, error: "JOIN_FAILED" });
    }
  });
}

/**
 * Leave one protected work-item room.
 *
 * Behavior:
 * - remove desired intent immediately
 * - if socket offline, that is already enough
 * - if online, emit leave
 */
function leaveWorkItem(workItemKey) {
  const key = normalizeWorkItemKey(workItemKey);
  if (!key) {
    return Promise.resolve({ ok: false, error: "INVALID_KEY" });
  }

  desiredWorkItemKeys.delete(key);

  const s = ensureSocket();

  if (!s.connected) {
    return Promise.resolve({
      ok: true,
      queued: true,
      workItemKey: key,
    });
  }

  return new Promise((resolve) => {
    try {
      s.emit(SOCKET_EVENTS.LEAVE_WORK_ITEM, { workItemKey: key }, (ack) => {
        resolve(ack || { ok: true });
      });
    } catch {
      resolve({ ok: false, error: "LEAVE_FAILED" });
    }
  });
}

/**
 * Join many protected work-item rooms sequentially.
 *
 * Sequential processing is acceptable here because:
 * - joins are lightweight
 * - logs/debugging stay easier to reason about
 */
async function joinWorkItems(keys = []) {
  const list = Array.isArray(keys) ? keys : [];
  const results = [];

  for (const k of list) {
    // eslint-disable-next-line no-await-in-loop
    results.push(await joinWorkItem(k));
  }

  return results;
}

/* -------------------------------------------------------------------------- */
/* Diagnostics                                                                */
/* -------------------------------------------------------------------------- */

/**
 * Diagnostic snapshot useful in dev tools or temporary troubleshooting.
 */
function getCurrentAuthSnapshot() {
  const sessionId = peekTabSessionId();
  const liveAuth = socket?.auth || null;

  return {
    sessionId,
    deviceId: getDeviceId(),
    browserId: getBrowserId(),
    desiredConnected,
    isConnected: Boolean(socket && socket.connected),
    reconnectGeneration,
    desiredRooms: Array.from(desiredWorkItemKeys),
    socketAuth: liveAuth
      ? {
          sessionId: liveAuth.sessionId || null,
          deviceId: liveAuth.deviceId || null,
          browserId: liveAuth.browserId || null,
        }
      : null,
  };
}

/**
 * Force one clean Socket.IO reconnect after apiClient observed that this tab is
 * talking to a different backend process runtime.
 *
 * Why this exists:
 * - Backend Socket.IO rooms are in-memory process state.
 * - After a backend restart, an already-open tab may still have a valid REST
 *   session but its old WebSocket may be unknown to the new backend process.
 * - A normal route switch often fixes this because the frontend re-enters its
 *   socket activation path. This helper makes that activation explicit without
 *   adding polling, hidden navigation or backend business traffic.
 *
 * Safety:
 * - It uses only the current tab-local session selector.
 * - It never invents auth or attempts cross-tab recovery.
 * - It does not fetch /my-page; Dashboard remains responsible for authoritative
 *   data refreshes after connect/reconnect.
 */
function handleBackendRuntimeChanged(detail = {}) {
  const runtimeId = String(detail?.runtimeId || "").trim();
  const now = Date.now();

  if (!runtimeId) {
    return { ok: false, reason: "missing-runtime-id" };
  }

  if (runtimeId === lastHandledBackendRuntimeId) {
    return { ok: true, reason: "runtime-already-handled" };
  }

  if (now - lastBackendRuntimeReconnectAt < BACKEND_RUNTIME_RECONNECT_THROTTLE_MS) {
    return { ok: false, reason: "runtime-reconnect-throttled" };
  }

  const nextAuth = buildAuthPayload();

  if (!canConnectWithAuth(nextAuth)) {
    if (DEV) {
      dwarn("[socketClient] backend runtime changed but this tab has no valid socket auth", {
        runtimeId,
        sessionId: nextAuth?.sessionId || null,
      });
    }
    return { ok: false, reason: "missing-auth" };
  }

  lastHandledBackendRuntimeId = runtimeId;
  lastBackendRuntimeReconnectAt = now;
  desiredConnected = true;
  reconnectGeneration += 1;

  const s = ensureSocket();
  s.auth = nextAuth;

  if (DEV) {
    dwarn("[socketClient] backend runtime changed -> forcing clean socket reconnect", {
      runtimeId,
      previousRuntimeId: detail?.previousRuntimeId || null,
      startedAt: detail?.startedAt || null,
      processId: detail?.processId || null,
      socketId: s.id || null,
      wasConnected: Boolean(s.connected),
      generation: reconnectGeneration,
    });
  }

  /**
   * Keep the same Socket.IO instance so existing React listeners remain
   * attached. We only force a new transport connection/handshake, which is the
   * part that must be rebuilt after backend process restart.
   */
  try {
    if (s.connected) {
      s.disconnect();
    }
  } catch {
    // ignore transient disconnect failures
  }

  try {
    s.auth = buildAuthPayload();
  } catch {
    // keep the already prepared auth payload
  }

  s.connect();

  return { ok: true, reason: "reconnect-started", runtimeId };
}

/* -------------------------------------------------------------------------- */
/* Exports                                                                    */
/* -------------------------------------------------------------------------- */

export default {
  connect,
  disconnect,
  refreshAuthAndReconnect,
  handleBackendRuntimeChanged,

  on,

  joinWorkItem,
  leaveWorkItem,
  joinWorkItems,

  get isConnected() {
    return Boolean(socket && socket.connected);
  },

  get wantsConnection() {
    return desiredConnected;
  },

  getCurrentAuthSnapshot,
  normalizeWorkItemKey,
};
